import {MouseEvent, ReactElement, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {MenuItem} from '@material-ui/core';
import ButtonBase from '@material-ui/core/ButtonBase';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import BusinessIcon from '@material-ui/icons/Business';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import PersonIcon from '@material-ui/icons/Person';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {COMPANY_DETAIL} from 'doctor/routing/paths';
import User from 'interfaces/User';
import {USER_PROFILE, LOGOUT} from 'shared/routing/paths';
import StyledMenu from './StyledMenu';

const useMenuItemStyles = makeStyles(theme => ({
  root: {
    '&:focus': {
      backgroundColor: theme.palette.primary.main,
      '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
        color: theme.palette.common.white
      }
    }
  }
}));

const useStyles = makeStyles(theme => ({
  usernameButton: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(2),
    maxWidth: '200px',
    [theme.breakpoints.up('sm')]: {
      maxWidth: 'none'
    }
  },
  usernameButtonInner: {
    whiteSpace: 'nowrap',
    overflow: 'hidden'
  },
  username: {
    fontSize: theme.typography.body1.fontSize,
    [theme.breakpoints.up('sm')]: {
      fontSize: theme.typography.h4.fontSize
    }
  }
}));

type Props = {
  fullname: string;
  isDoctor: boolean;
  doctorIsAdmin: boolean;
};

export default function Menu({fullname, isDoctor, doctorIsAdmin}: Props): ReactElement {
  const [userMenuElement, setUserMenuElement] = useState<HTMLElement | null>(null);

  const {t} = useTranslation(['menu']);
  const menuItemClasses = useMenuItemStyles();
  const classes = useStyles();

  function openUserMenu(event: MouseEvent<HTMLElement>) {
    setUserMenuElement(event.currentTarget);
  }

  function closeUserMenu() {
    setUserMenuElement(null);
  }

  return (
    <div>
      <ButtonBase className={classes.usernameButton} onClick={openUserMenu}>
        <div className={classes.usernameButtonInner}>
          <Typography variant="h4" component="p" className={classes.username}>
            {fullname}
          </Typography>
        </div>
        <KeyboardArrowDownIcon />
      </ButtonBase>

      <StyledMenu
        id="user-menu"
        anchorEl={userMenuElement}
        keepMounted
        open={Boolean(userMenuElement)}
        onClose={closeUserMenu}
      >
        <MenuItem
          classes={menuItemClasses}
          component={Link}
          to={USER_PROFILE}
          onClick={closeUserMenu}
        >
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('menu:profile')} />
        </MenuItem>
        {isDoctor && doctorIsAdmin && (
          <MenuItem
            classes={menuItemClasses}
            component={Link}
            to={COMPANY_DETAIL}
            onClick={closeUserMenu}
          >
            <ListItemIcon>
              <BusinessIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('menu:medicalCenter')} />
          </MenuItem>
        )}
        <MenuItem classes={menuItemClasses} component={Link} to={LOGOUT} onClick={closeUserMenu}>
          <ListItemIcon>
            <ExitToAppIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('menu:logout')} />
        </MenuItem>
      </StyledMenu>
    </div>
  );
}
